import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowLeft, Dumbbell, Play, Check, Clock, Calendar, ChevronRight,
  Zap, Target, Layers, Trophy, X
} from 'lucide-react';

const TIPO_CONFIG = {
  tren_superior: { label: 'Tren Superior', icon: <Zap className="w-5 h-5" />, color: 'from-blue-600 to-cyan-500', accent: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500/10' },
  tren_inferior: { label: 'Tren Inferior', icon: <Target className="w-5 h-5" />, color: 'from-orange-600 to-red-500', accent: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/10' },
  full_body: { label: 'Full Body', icon: <Layers className="w-5 h-5" />, color: 'from-purple-600 to-pink-500', accent: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/10' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ================ WORKOUT EXECUTION VIEW ================
function WorkoutView({ asignacion, onFinish, onBack }) {
  const [ejercicios, setEjercicios] = useState(() =>
    (asignacion.ejercicios || []).map(ej => ({
      ...ej,
      sets: Array.from({ length: ej.series || 3 }, (_, i) => ({
        numero: i + 1, peso: '', reps: ej.reps || 10, done: false
      }))
    }))
  );
  const [saving, setSaving] = useState(false);
  const [restTimer, setRestTimer] = useState(null);
  const [restSeconds, setRestSeconds] = useState(0);

  // Rest timer
  useEffect(() => {
    if (restTimer === null) return;
    if (restSeconds <= 0) { setRestTimer(null); return; }
    const t = setTimeout(() => setRestSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [restSeconds, restTimer]);

  const startRest = (seconds = 90) => { setRestTimer(Date.now()); setRestSeconds(seconds); };

  const updateSet = (ejIdx, setIdx, field, value) => {
    setEjercicios(prev => {
      const copy = [...prev];
      copy[ejIdx] = { ...copy[ejIdx], sets: [...copy[ejIdx].sets] };
      copy[ejIdx].sets[setIdx] = { ...copy[ejIdx].sets[setIdx], [field]: value };
      return copy;
    });
  };

  const toggleSetDone = (ejIdx, setIdx) => {
    updateSet(ejIdx, setIdx, 'done', !ejercicios[ejIdx].sets[setIdx].done);
    if (!ejercicios[ejIdx].sets[setIdx].done) startRest(90);
  };

  const allDone = ejercicios.every(ej => ej.sets.every(s => s.done));

  const handleFinish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        rutina_id: asignacion.rutina_id,
        tipo: asignacion.rutina_tipo,
        nombre_rutina: asignacion.rutina_nombre,
        ejercicios: ejercicios.map(ej => ({
          nombre: ej.nombre,
          series_config: ej.series || 3,
          reps_config: ej.reps || 10,
          sets: ej.sets.filter(s => s.done).map(s => ({
            numero: s.numero,
            peso: parseFloat(s.peso) || 0,
            reps: parseInt(s.reps) || ej.reps || 10
          }))
        }))
      };
      await fetch(`${API}/entrenamientos-fuerza`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      onFinish();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const tipoConf = TIPO_CONFIG[asignacion.rutina_tipo] || TIPO_CONFIG.full_body;

  return (
    <div data-testid="workout-view">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="text-gray-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-white">{asignacion.rutina_nombre}</h2>
          <span className={`text-xs ${tipoConf.accent}`}>{tipoConf.label}</span>
        </div>
      </div>

      {/* Rest Timer */}
      {restTimer !== null && restSeconds > 0 && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-mono text-lg font-bold">
              {Math.floor(restSeconds / 60)}:{(restSeconds % 60).toString().padStart(2, '0')}
            </span>
            <span className="text-gray-400 text-xs">descanso</span>
          </div>
          <button onClick={() => { setRestTimer(null); setRestSeconds(0); }} className="text-gray-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Exercises */}
      <div className="space-y-4">
        {ejercicios.map((ej, ejIdx) => (
          <Card key={ejIdx} className={`bg-gray-900/60 border ${tipoConf.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Dumbbell className={`w-4 h-4 ${tipoConf.accent}`} />
                <span className="text-white font-semibold text-sm">{ej.nombre}</span>
                <span className="text-gray-500 text-xs ml-auto">{ej.series}x{ej.reps}</span>
              </div>
              {/* Sets */}
              <div className="space-y-2">
                <div className="grid grid-cols-[40px_1fr_1fr_40px] gap-2 text-xs text-gray-500 px-1">
                  <span>Set</span><span>Peso (kg)</span><span>Reps</span><span></span>
                </div>
                {ej.sets.map((set, setIdx) => (
                  <div key={setIdx} className={`grid grid-cols-[40px_1fr_1fr_40px] gap-2 items-center ${set.done ? 'opacity-60' : ''}`}>
                    <span className="text-gray-400 text-sm text-center">{set.numero}</span>
                    <input
                      type="number"
                      value={set.peso}
                      onChange={e => updateSet(ejIdx, setIdx, 'peso', e.target.value)}
                      placeholder="0"
                      className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm text-center w-full"
                      data-testid={`set-peso-${ejIdx}-${setIdx}`}
                    />
                    <input
                      type="number"
                      value={set.reps}
                      onChange={e => updateSet(ejIdx, setIdx, 'reps', e.target.value)}
                      className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm text-center w-full"
                      data-testid={`set-reps-${ejIdx}-${setIdx}`}
                    />
                    <button
                      onClick={() => toggleSetDone(ejIdx, setIdx)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                        set.done ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-500 hover:text-white'
                      }`}
                      data-testid={`set-done-${ejIdx}-${setIdx}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Finish button */}
      <div className="mt-6 pb-8">
        <Button
          onClick={handleFinish}
          disabled={saving || !ejercicios.some(ej => ej.sets.some(s => s.done))}
          className={`w-full py-3 font-bold text-black ${allDone ? 'bg-green-500 hover:bg-green-400' : 'bg-gold hover:bg-gold/80'}`}
          data-testid="btn-finish-workout"
        >
          {saving ? 'Guardando...' : allDone ? 'Completar Sesion' : 'Guardar Progreso'}
        </Button>
      </div>
    </div>
  );
}

// ================ ROUTINE DETAIL VIEW ================
function RutinaDetalle({ asignacion, onBack, onRefresh }) {
  const [workout, setWorkout] = useState(false);
  const tipoConf = TIPO_CONFIG[asignacion.rutina_tipo] || TIPO_CONFIG.full_body;

  if (workout) {
    return (
      <WorkoutView
        asignacion={asignacion}
        onBack={() => setWorkout(false)}
        onFinish={() => { setWorkout(false); onRefresh(); }}
      />
    );
  }

  return (
    <div data-testid="rutina-detalle">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="text-gray-400 hover:text-white" data-testid="btn-back-detail">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-white">{asignacion.rutina_nombre}</h2>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs px-2 py-0.5 rounded-full ${tipoConf.bg} ${tipoConf.accent}`}>{tipoConf.label}</span>
            {asignacion.notas && <span className="text-xs text-gray-400">· {asignacion.notas}</span>}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-3 text-center">
          <div className="text-xl font-bold text-green-400">{asignacion.sesiones_completadas || 0}</div>
          <div className="text-xs text-gray-400">Sesiones completadas</div>
        </div>
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-3 text-center">
          <div className="text-xl font-bold text-gray-300">{formatDate(asignacion.fecha_asignacion)}</div>
          <div className="text-xs text-gray-400">Asignada</div>
        </div>
      </div>

      {/* Exercises list */}
      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Ejercicios</h3>
      <div className="space-y-2 mb-6">
        {(asignacion.ejercicios || []).map((ej, i) => (
          <div key={i} className={`flex items-center gap-3 p-3 bg-gray-900/60 rounded-xl border ${tipoConf.border}`}>
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${tipoConf.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white">{ej.nombre}</div>
              <div className="text-xs text-gray-400">{ej.series} series x {ej.reps} reps</div>
            </div>
          </div>
        ))}
      </div>

      {/* Workout History */}
      {asignacion.historial && asignacion.historial.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Historial</h3>
          <div className="space-y-2">
            {asignacion.historial.slice(0, 5).map((h, i) => (
              <div key={i} className="flex items-center gap-3 p-2 bg-gray-900/40 rounded-lg">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-sm text-white">{h.fecha}</span>
                  <span className="text-xs text-gray-500 ml-2">{h.total_ejercicios} ejercicios</span>
                </div>
                {h.records_rotos > 0 && (
                  <div className="flex items-center gap-1 text-amber-400 text-xs">
                    <Trophy className="w-3 h-3" /> {h.records_rotos}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Start Workout Button */}
      <Button
        onClick={() => setWorkout(true)}
        className="w-full py-3 bg-gold text-black font-bold hover:bg-gold/80"
        data-testid="btn-start-workout"
      >
        <Play className="w-4 h-4 mr-2" /> Empezar Sesion
      </Button>
    </div>
  );
}

// ================ MAIN PAGE ================
export default function MisRutinas() {
  const { user } = useAuth();
  const [rutinas, setRutinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const token = localStorage.getItem('token');

  const fetchRutinas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/client-plus/rutinas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setRutinas(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchRutinas(); }, [fetchRutinas]);

  const openDetail = async (asignacion) => {
    try {
      const res = await fetch(`${API}/client-plus/rutina/${asignacion.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDetailData(await res.json());
        setSelected(asignacion.id);
      }
    } catch (e) { console.error(e); }
  };

  // Detail view
  if (selected && detailData) {
    return (
      <div data-testid="mis-rutinas-page">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 pt-6 pb-20">
          <RutinaDetalle
            asignacion={detailData}
            onBack={() => { setSelected(null); setDetailData(null); }}
            onRefresh={() => { fetchRutinas(); openDetail({ id: selected }); }}
          />
        </div>
      </div>
    );
  }

  // List view
  return (
    <div data-testid="mis-rutinas-page">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 pt-6 pb-20">
        <div className="flex items-center gap-3 mb-6">
          <Dumbbell className="w-6 h-6 text-gold" />
          <h1 className="text-xl font-bold text-gold">Mis Rutinas Asignadas</h1>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gold mx-auto mb-3"></div>
            <p className="text-gray-400 text-sm">Cargando rutinas...</p>
          </div>
        ) : rutinas.length === 0 ? (
          <div className="text-center py-12 bg-gray-900/40 rounded-xl border border-gray-700">
            <Dumbbell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Aun no tienes rutinas asignadas</p>
            <p className="text-gray-500 text-sm mt-1">Tu entrenador te asignara rutinas pronto</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rutinas.map(r => {
              const tipoConf = TIPO_CONFIG[r.rutina_tipo] || TIPO_CONFIG.full_body;
              return (
                <button
                  key={r.id}
                  onClick={() => openDetail(r)}
                  className={`w-full text-left p-4 rounded-xl border ${tipoConf.border} bg-gray-900/60 hover:bg-gray-800/80 transition-all group`}
                  data-testid={`rutina-card-${r.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tipoConf.color} flex items-center justify-center flex-shrink-0`}>
                      {tipoConf.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white">{r.rutina_nombre}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs ${tipoConf.accent}`}>{tipoConf.label}</span>
                        <span className="text-xs text-gray-500">·</span>
                        <span className="text-xs text-gray-400">{(r.ejercicios || []).length} ejercicios</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <Check className="w-3 h-3" /> {r.sesiones_completadas} sesiones
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatDate(r.fecha_asignacion)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
